import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api, configurationReady, demoMode } from '../lib/api';
import { normalizeNickname } from '../lib/validation';
import type { EventSnapshot } from '../types';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { useFlowerQueue } from '../hooks/useFlowerQueue';
import {
  readFollowPreference,
  writeFollowPreference,
} from '../lib/presenterFollow';

const NICKNAME_KEY = 'panther-2026:nickname';

interface EventContextValue {
  snapshot: EventSnapshot | null;
  loading: boolean;
  error: string | null;
  online: boolean;
  demoMode: boolean;
  nickname: string;
  flowerError: string | null;
  flowerSaving: boolean;
  followPresenter: boolean;
  hasCommentDraft: boolean;
  setNickname(value: string): void;
  setFollowPresenter(value: boolean): void;
  setHasCommentDraft(value: boolean): void;
  queueFlower(topicId: string): void;
  pendingFlowersFor(topicId: string): number;
  refresh(): Promise<void>;
  updateTopic(
    topicId: string,
    changes: Partial<Pick<EventSnapshot['topics'][number], 'likeCount' | 'flowerCount' | 'commentCount'>>,
  ): void;
}

const EventContext = createContext<EventContextValue | null>(null);

export function EventProvider({ children }: { children: ReactNode }) {
  const [snapshot, setSnapshot] = useState<EventSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nickname, setNicknameState] = useState(
    () => localStorage.getItem(NICKNAME_KEY) ?? '',
  );
  const [flowerError, setFlowerError] = useState<string | null>(null);
  const [followPresenter, setFollowPresenterState] = useState(() =>
    readFollowPreference(localStorage),
  );
  const [hasCommentDraft, setHasCommentDraft] = useState(false);
  const online = useOnlineStatus();
  const mounted = useRef(true);
  const generation = snapshot?.settings.interactionGeneration ?? null;
  const currentGeneration = useRef(generation);
  currentGeneration.current = generation;

  const refresh = useCallback(async () => {
    if (!configurationReady) {
      setError('This app is not connected to Supabase yet.');
      setLoading(false);
      return;
    }
    try {
      const next = await api.getSnapshot();
      if (!mounted.current) return;
      setSnapshot((current) =>
        current && current.settings.interactionGeneration > next.settings.interactionGeneration
          ? current : next,
      );
      setError(null);
    } catch {
      if (mounted.current) {
        setError('Shared event data is unavailable. We will keep trying.');
      }
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    const start = async () => {
      if (!configurationReady) {
        setLoading(false);
        setError('This app is not connected to Supabase yet.');
        return;
      }
      try {
        await api.ensureSession();
        await refresh();
      } catch {
        if (mounted.current) {
          setLoading(false);
          setError('Anonymous sign-in failed. Refresh to try again.');
        }
      }
    };
    void start();
    return () => {
      mounted.current = false;
    };
  }, [refresh]);

  useEffect(() => {
    if (!configurationReady) return;
    let timer = 0;
    const schedule = () => {
      const delay = 8_000 + Math.floor(Math.random() * 4_001);
      timer = window.setTimeout(async () => {
        if (document.visibilityState === 'visible' && navigator.onLine) {
          await refresh();
        }
        schedule();
      }, delay);
    };
    schedule();
    return () => window.clearTimeout(timer);
  }, [refresh]);

  const setNickname = useCallback((value: string) => {
    const safe = value.slice(0, 40);
    setNicknameState(safe);
    if (safe.trim()) localStorage.setItem(NICKNAME_KEY, safe);
    else localStorage.removeItem(NICKNAME_KEY);
  }, []);

  const setFollowPresenter = useCallback((value: boolean) => {
    setFollowPresenterState(value);
    writeFollowPreference(localStorage, value);
  }, []);

  const updateTopic = useCallback<EventContextValue['updateTopic']>(
    (topicId, changes) => {
      if (generation !== currentGeneration.current) return;
      setSnapshot((current) =>
        current
          ? {
              ...current,
              topics: current.topics.map((topic) =>
                topic.id === topicId ? { ...topic, ...changes } : topic,
              ),
            }
          : current,
      );
    },
    [generation],
  );
  const onFlowerApplied = useCallback(
    (receipt: { topicId: string; flowerCount: number }) => {
      updateTopic(receipt.topicId, { flowerCount: receipt.flowerCount });
      setFlowerError(null);
    },
    [updateTopic],
  );
  const flowerQueue = useFlowerQueue({
    generation,
    onApplied: onFlowerApplied,
    onError: setFlowerError,
  });
  const queueFlower = useCallback(
    (topicId: string) => {
      try {
        flowerQueue.addFlower(topicId);
        setFlowerError(null);
      } catch (queueError) {
        setFlowerError(
          queueError instanceof Error ? queueError.message : 'Flower was not queued.',
        );
      }
    },
    [flowerQueue],
  );

  const value = useMemo<EventContextValue>(
    () => ({
      snapshot,
      loading,
      error,
      online,
      demoMode,
      nickname,
      flowerError,
      flowerSaving: flowerQueue.saving,
      followPresenter,
      hasCommentDraft,
      setNickname,
      setFollowPresenter,
      setHasCommentDraft,
      queueFlower,
      pendingFlowersFor: flowerQueue.pendingFor,
      refresh,
      updateTopic,
    }),
    [
      snapshot,
      loading,
      error,
      online,
      nickname,
      flowerError,
      followPresenter,
      hasCommentDraft,
      flowerQueue.pendingFor,
      flowerQueue.saving,
      setNickname,
      setFollowPresenter,
      queueFlower,
      refresh,
      updateTopic,
    ],
  );

  return <EventContext.Provider value={value}>{children}</EventContext.Provider>;
}

export function useEvent(): EventContextValue {
  const context = useContext(EventContext);
  if (!context) throw new Error('useEvent must be used within EventProvider.');
  return context;
}

export function displayNickname(value: string): string {
  return normalizeNickname(value);
}
